import { useForm } from '@inertiajs/react'
import { ArrowRight, Loader2, User, Building2 } from 'lucide-react'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type CompleteProps = {
  identity: {
    email_address: string
  }
}

export default function RegistrationsComplete({ identity }: CompleteProps) {
  const { data, setData, post, processing, errors } = useForm({
    name: '',
    account_name: ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/registration/finish')
  }

  return (
    <AuthLayout
      title="Set Up Your Workspace"
      heading="Set up your workspace"
      description="Tell us a bit about yourself to get started."
    >
      <div className="mb-6 flex items-center gap-2.5 py-2.5 px-3.5 rounded-lg bg-muted/60 border border-border/60">
        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <span className="text-[11px] font-bold text-primary">
            {identity.email_address.charAt(0).toUpperCase()}
          </span>
        </div>
        <span className="text-[13px] text-muted-foreground truncate">{identity.email_address}</span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-[13px] font-medium text-foreground/70">
            Full name
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/40" />
            <Input
              id="name"
              type="text"
              placeholder="Jane Doe"
              value={data.name}
              onChange={e => setData('name', e.target.value)}
              autoComplete="name"
              autoFocus
              required
              className="h-11 pl-10"
            />
          </div>
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="account_name" className="text-[13px] font-medium text-foreground/70">
            Workspace name
          </Label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/40" />
            <Input
              id="account_name"
              type="text"
              placeholder="Acme Inc."
              value={data.account_name}
              onChange={e => setData('account_name', e.target.value)}
              required
              className="h-11 pl-10"
            />
          </div>
          <p className="text-[11px] text-muted-foreground/60 pl-0.5">
            The name of your company or team. You can change this anytime.
          </p>
          {errors.account_name && (
            <p className="text-sm text-destructive">{errors.account_name}</p>
          )}
        </div>

        <Button type="submit" className="w-full h-11 text-sm font-semibold gap-2" disabled={processing}>
          {processing ? (
            <>
              <Loader2 className="animate-spin h-4 w-4" />
              Creating workspace...
            </>
          ) : (
            <>
              Continue
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </form>
    </AuthLayout>
  )
}
